import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import AuthTabs from '../components/AuthTabs';
import { usePageMeta } from '../hooks/usePageMeta';
import { REFERRAL_CODE_KEY } from '../services/api';
import {
  ArrowRight, Sparkles, BarChart3, RefreshCw, Check, ChevronDown,
  Star, Download, FileText, Users, Clock, TrendingUp,
  Target, AlertTriangle, Zap, Menu, X, UserPlus,
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Features', href: '#features' },
  { label: 'Templates', href: '#templates' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

const FREE_TEMPLATES = new Set(['modern', 'classic', 'minimalist', 'glassmorphism', 'minimalist-dark']);

const TPL_NAMES: Record<string, string> = {
  modern: 'Modern Resume',
  classic: 'Classic Resume',
  minimalist: 'Minimalist Resume',
  glassmorphism: 'Glassmorphism Resume',
  'minimalist-dark': 'Minimalist Dark Resume',
  'terminal-inspired': 'Terminal Inspired Resume',
};

const TPL_FALLBACK = [
  { id: 'classic', name: 'Classic Resume' },
  { id: 'modern', name: 'Modern Resume' },
  { id: 'minimalist', name: 'Minimalist Resume' },
  { id: 'glassmorphism', name: 'Glassmorphism Resume' },
  { id: 'minimalist-dark', name: 'Minimalist Dark Resume' },
  { id: 'terminal-inspired', name: 'Terminal Inspired Resume' },
];

interface Plan {
  id: string;
  name: string;
  amount: number;
  currency: string;
  interval: string;
}

const Landing: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [templates, setTemplates] = useState<string[]>([]);
  const [templateThumbnails, setTemplateThumbnails] = useState<Record<string, string>>({});
  const [templateFreeSet, setTemplateFreeSet] = useState<Set<string>>(new Set());
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [hasReferralCode, setHasReferralCode] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const RESUME_API_URL = import.meta.env.VITE_RESUME_API_URL || 'https://3atech.de/api/v1/resumes';
    fetch(`${RESUME_API_URL}/thumbnails`, { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then((data: Array<{ id: string; name: string; thumbnailUrl: string | null; free: boolean }>) => {
        setTemplates(data.map(t => t.id));
        setTemplateFreeSet(new Set(data.filter(t => t.free).map(t => t.id)));
        const origin = RESUME_API_URL.replace(/\/api\/v1\/resumes$/, '');
        const map: Record<string, string> = {};
        data.forEach(t => {
          if (t.thumbnailUrl) {
            map[t.id] = t.thumbnailUrl.startsWith('http') ? t.thumbnailUrl : `${origin}${t.thumbnailUrl}`;
          }
        });
        setTemplateThumbnails(map);
      })
      .catch(() => {
        setTemplates([]);
      });
  }, []);

  useEffect(() => {
    fetch('/billing/api/v1/billing/plans', { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then((data: { plans: Plan[] }) => setPlans(data.plans))
      .catch(() => {})
      .finally(() => setLoadingPlans(false));

    fetch('/billing/api/v1/billing/payments-enabled', { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then((data: { enabled: boolean }) => setPaymentsEnabled(data.enabled))
      .catch(() => {});
  }, []);

  const scrollToAuth = useCallback((tab: 'login' | 'register') => {
    setAuthTab(tab);
    const el = document.getElementById('auth-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setMobileMenuOpen(false);
  }, []);

  const scrollTo = useCallback((href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  }, []);

  const { scrollYProgress } = useScroll();
  const navBg = useTransform(scrollYProgress, [0, 0.05], ['rgba(255,255,255,0)', 'rgba(255,255,255,0.8)']);

  usePageMeta({
    title: 'Resume Builder - 3atech | Your trusted resume builder',
    description:
      'Create a professional resume free with 3A Tech. AI-powered resume builder with ATS-friendly templates, job matching, and instant PDF download.',
  });

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && ref.trim()) {
      localStorage.setItem(REFERRAL_CODE_KEY, ref.trim());
      setHasReferralCode(true);
    }
    if (!isAuthenticated && searchParams.get('ref') !== null) {
      searchParams.delete('ref');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const monthlyPlan = plans.find(p => p.id === 'pro-monthly');
  const yearlyPlan = plans.find(p => p.id === 'pro-yearly');
  const activePlan = billingInterval === 'month' ? monthlyPlan : yearlyPlan;
  const savingsPct = monthlyPlan?.amount && yearlyPlan?.amount
    ? Math.round((1 - yearlyPlan.amount / (monthlyPlan.amount * 12)) * 100) : 0;
  const planAmount = activePlan?.amount ?? (billingInterval === 'month' ? 9.99 : 19.99);
  const currencySymbol = (activePlan?.currency ?? 'EUR') === 'EUR' ? '€' : '$';
  const planInterval = billingInterval === 'month' ? 'month' : 'year';
  const priceDisplay = Number.isInteger(planAmount) ? `${currencySymbol}${planAmount}` : `${currencySymbol}${planAmount.toFixed(2)}`;

  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="w-6 h-6 border-[3px] border-zinc-200 border-t-brand-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-page min-h-screen font-body text-foreground">
      {/* ============ NAV ============ */}
      <motion.nav
        style={{ background: navBg }}
        className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-200/50 backdrop-blur-xl"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold text-lg tracking-tight">ResumeAI</span>
          </div>

          <div className="hidden md:flex items-center gap-1">
            {(paymentsEnabled ? NAV_ITEMS : NAV_ITEMS.filter(i => i.href !== '#pricing')).map((item) => (
              <button
                key={item.href}
                onClick={() => scrollTo(item.href)}
                className="px-3 py-2 text-sm text-zinc-500 hover:text-foreground transition-colors rounded-btn hover:bg-zinc-100"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => scrollToAuth('login')}
              className="px-4 py-2 text-sm text-zinc-600 hover:text-foreground transition-colors font-medium"
            >
              Sign In
            </button>
            <button
              onClick={() => scrollToAuth('register')}
              className="h-9 px-4 bg-foreground hover:bg-foreground/90 text-white text-sm font-medium rounded-btn transition-all duration-200 flex items-center gap-2"
            >
              Build My Resume
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center text-zinc-600 hover:text-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-zinc-200 bg-white px-4 py-4 space-y-2"
            >
              {(paymentsEnabled ? NAV_ITEMS : NAV_ITEMS.filter(i => i.href !== '#pricing')).map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollTo(item.href)}
                  className="block w-full text-left px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 rounded-btn"
                >
                  {item.label}
                </button>
              ))}
              <hr className="border-zinc-100" />
              <button
                onClick={() => scrollToAuth('login')}
                className="block w-full text-left px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 rounded-btn"
              >
                Sign In
              </button>
              <button
                onClick={() => scrollToAuth('register')}
                className="w-full h-10 bg-foreground text-white text-sm font-medium rounded-btn flex items-center justify-center gap-2"
              >
                Build My Resume
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      <main>
      {/* ============ HERO ============ */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-50 border border-brand-100 rounded-full text-brand-500 text-xs font-medium mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                AI-Powered Resume Builder
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.1] text-foreground mb-5">
                Build resumes that{' '}
                <span className="text-brand-500">land interviews</span>
                , automated by AI.
              </h1>
              <p className="text-lg text-zinc-500 leading-relaxed max-w-xl mb-8">
                Transform your raw work history into an elite, ATS-optimized resume in seconds.
                Engineered for modern professionals.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <button
                  onClick={() => scrollToAuth('register')}
                  className="h-11 px-6 bg-foreground hover:bg-foreground/90 text-white text-sm font-medium rounded-btn transition-all duration-200 flex items-center justify-center gap-2"
                >
                  Generate Free Resume
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollTo('#features')}
                  className="h-11 px-6 border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-sm font-medium rounded-btn transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Star className="w-4 h-4" />
                  Watch Product Demo
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-zinc-200 border-2 border-white flex items-center justify-center text-[10px] font-medium text-zinc-500"
                    >
                      {['G', 'M', 'S', 'V'][i]}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-secondary-foreground">
                  <span className="text-zinc-600 font-medium">Engineers</span> at top tech companies use this builder
                </p>
              </div>
            </div>

            <div id="auth-section" className="scroll-mt-24">
              {hasReferralCode && (
                <div className="mb-4 rounded-btn bg-brand-50 text-brand-700 px-4 py-2 text-sm font-medium flex items-center gap-2 border border-brand-200">
                  <UserPlus className="w-4 h-4" />
                  You were invited by a friend — your referral is recorded
                </div>
              )}
              <AuthTabs activeTab={authTab} onTabChange={setAuthTab} />
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRODUCT SHOWCASE ============ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
              See the transformation
            </h2>
            <p className="text-zinc-500 max-w-xl mx-auto">
              Vague bullet points become compelling impact statements — without rewriting from scratch.
            </p>
          </div>

          <div className="bg-white rounded-card border border-zinc-200 shadow-elevated overflow-hidden">
            <div className="grid md:grid-cols-[1fr_auto_1fr] items-stretch">
              {/* RAW */}
              <div className="p-6 sm:p-8 lg:p-10 bg-zinc-50/50">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                  </div>
                  <span className="text-xs font-semibold text-secondary-foreground tracking-wider uppercase">Your First Draft</span>
                </div>
                <div className="space-y-2.5">
                  {[
                    ['fixed database bugs', 'Resolved MySQL deadlock issues in payment pipeline'],
                    ['helped with standups', 'Participated in daily scrum ceremonies'],
                    ['worked on frontend', 'Implemented UI components in React'],
                    ['attended meetings', 'Contributed to sprint retrospectives'],
                  ].map(([raw, _], i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08, duration: 0.3 }}
                      className="group relative"
                    >
                      <div className="h-9 bg-white border border-zinc-200 rounded-btn flex items-center px-3 text-sm text-secondary-foreground font-mono">
                        <span className="line-through decoration-zinc-300 decoration-1">{raw}</span>
                      </div>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-zinc-200 border-2 border-white flex items-center justify-center hidden md:flex">
                        <ArrowRight className="w-3 h-3 text-zinc-400" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* DIVIDER */}
              <div className="hidden md:flex items-center justify-center bg-gradient-to-b from-brand-100/50 via-brand-500/10 to-brand-100/50 px-2">
                <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center shadow-lg shadow-brand-200">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              </div>

              {/* AI OPTIMIZED */}
              <div className="p-6 sm:p-8 lg:p-10 bg-gradient-to-br from-brand-50/30 via-white to-brand-50/30 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-500/3 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                <div className="flex items-center gap-2 mb-5 relative">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 tracking-wider uppercase">AI-Enhanced</span>
                  <span className="ml-auto text-[10px] font-medium text-brand-500 bg-brand-50 px-2 py-0.5 rounded-full">ATS Scored: 92</span>
                </div>
                <div className="space-y-2.5 relative">
                  {[
                    'Resolved critical MySQL deadlock contentions in payment pipeline, cutting P1 incidents by 73%',
                    'Steered daily standups and sprint ceremonies across 2 backend squads, improving delivery cadence by 34%',
                    'Architected 12+ reusable React components now adopted across 3 product lines',
                    'Drove data-driven retrospective action items that reduced sprint churn by 28%',
                  ].map((text, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1, duration: 0.35 }}
                      className="h-auto min-h-[36px] bg-white border border-brand-200 rounded-btn flex items-start px-3.5 py-2 text-sm text-foreground shadow-sm hover:shadow-md hover:border-brand-300 transition-all duration-200"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-1.5 mr-2.5 shrink-0" />
                      <span>{text}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-brand-100 flex items-center gap-2 text-[11px] text-secondary-foreground relative">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                  <span>
                    <span className="text-emerald-600 font-medium">+47%</span> improvement in recruiter engagement score
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section id="features" className="py-20 scroll-mt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
              Everything you need to land the role
            </h2>
            <p className="text-zinc-500 max-w-xl mx-auto">
              Intelligent tools that transform how you present your professional story.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Sparkles,
                title: 'AI-Powered Enrichment',
                desc: 'Turn bullet points into compelling impact statements that recruiters notice.',
                before: '"fixed database bugs"',
                after: '"Overhauled database indexing, reducing query latency by 42%"',
              },
              {
                icon: BarChart3,
                title: 'ATS Optimization Matrix',
                desc: 'Your resume formatted perfectly for every applicant tracking system.',
                before: 'Inconsistent formatting',
                after: 'Precision-engineered layout with 98% ATS pass rate',
              },
              {
                icon: RefreshCw,
                title: 'Live Template Hot-Swapping',
                desc: 'Switch between premium templates instantly — your content stays intact.',
                before: 'Static, one-size-fits-all designs',
                after: 'Adaptive layouts that reflow your data perfectly',
              },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-white border border-zinc-200 rounded-card p-6 hover:shadow-elevated transition-shadow duration-300"
              >
                <div className="w-10 h-10 bg-brand-50 rounded-btn flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-brand-500" />
                </div>
                <h3 className="font-display font-semibold text-lg text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-500 mb-4 leading-relaxed">{feature.desc}</p>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-secondary-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-zinc-300" />
                    <span className="line-through">{feature.before}</span>
                  </div>
                  <div className="flex items-center gap-2 text-brand-500">
                    <Check className="w-3 h-3" />
                    <span className="font-medium">{feature.after}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ JD MATCH SHOWCASE ============ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-zinc-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
              Know exactly where you stand
            </h2>
            <p className="text-zinc-500 max-w-xl mx-auto">
              Paste any job description and get a laser-focused gap analysis — skills, experience, keywords — before you apply.
            </p>
          </div>

          <div className="bg-gradient-to-br from-brand-50/40 via-white to-brand-50/20 rounded-card border border-zinc-200 shadow-elevated overflow-hidden">
            {/* Mock JD header */}
            <div className="px-6 sm:px-8 pt-8 pb-4 border-b border-zinc-100">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-medium text-emerald-600 tracking-wider uppercase">Live Analysis</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div>
                  <h3 className="font-display text-lg font-semibold text-foreground">Senior Frontend Engineer</h3>
                  <p className="text-sm text-secondary-foreground">Acme Corp · Remote · $150k-$190k</p>
                </div>
                <div className="sm:ml-auto flex items-center gap-2 text-xs text-secondary-foreground bg-zinc-50 border border-zinc-200 rounded-btn px-3 py-1.5">
                  <Target className="w-3.5 h-3.5 text-brand-500" />
                  <span>Your resume vs this role</span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-zinc-100">
              {/* Score ring */}
              <div className="md:col-span-1 p-6 sm:p-8 flex flex-col items-center justify-center">
                <div className="relative w-[110px] h-[110px] mb-3">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 110 110">
                    <circle cx="55" cy="55" r="48" fill="none" stroke="#E4E4E7" strokeWidth="6" />
                    <circle cx="55" cy="55" r="48" fill="none" stroke="#4F46E5" strokeWidth="6" strokeDasharray={`${(68 / 100) * 301.6} 301.6`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-display text-2xl font-bold text-foreground">68</span>
                    <span className="text-[10px] text-secondary-foreground font-medium">Match Score</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Room for improvement</span>
                </div>
              </div>

              {/* Skills comparison */}
              <div className="md:col-span-2 p-6 sm:p-8">
                <div className="text-xs font-semibold text-secondary-foreground tracking-wider uppercase mb-4">Skills Match</div>
                <div className="space-y-3">
                  {[
                    { name: 'React / Next.js', match: true },
                    { name: 'TypeScript', match: true },
                    { name: 'GraphQL', match: false },
                    { name: 'Cypress / Playwright', match: true },
                    { name: 'Docker / K8s', match: false },
                    { name: 'System Design', match: false },
                  ].map((skill) => (
                    <div key={skill.name} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${skill.match ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                        {skill.match ? (
                          <Check className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <div className="w-2 h-0.5 rounded bg-zinc-300" />
                        )}
                      </div>
                      <span className={`text-sm ${skill.match ? 'text-foreground' : 'text-secondary-foreground'}`}>{skill.name}</span>
                      {!skill.match && (
                        <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 ml-auto">Missing</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Optimization suggestions */}
              <div className="md:col-span-2 p-6 sm:p-8 bg-gradient-to-br from-brand-50/30 to-white">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-4 h-4 text-brand-500" />
                  <span className="text-xs font-semibold text-brand-500 tracking-wider uppercase">Optimization Plan</span>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Add GraphQL experience', detail: 'Highlight any API layer work — even REST-to-GraphQL migrations count.', priority: 'high' },
                    { label: 'Mention containerization', detail: 'List Docker Compose or Kubernetes exposure from CI/CD or dev env setup.', priority: 'high' },
                    { label: 'Feature system design skills', detail: 'Add a bullet about architectural decisions, scaling, or trade-off analysis.', priority: 'medium' },
                  ].map((tip) => (
                    <div key={tip.label} className="flex gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${tip.priority === 'high' ? 'bg-amber-500' : 'bg-brand-400'}`} />
                      <div>
                        <div className="text-sm font-medium text-foreground">{tip.label}</div>
                        <div className="text-xs text-secondary-foreground leading-relaxed mt-0.5">{tip.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-brand-100 flex items-center gap-2 text-xs text-secondary-foreground">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Addressing these gaps could raise your match score to <span className="text-emerald-600 font-semibold">91</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TEMPLATES MARQUEE ============ */}
      <section id="templates" className="py-20 scroll-mt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground text-center mb-4">
            Premium templates, crafted for professionals
          </h2>
          <p className="text-zinc-500 text-center max-w-xl mx-auto">
            From executive to creative — every template is ATS-optimized out of the box.
          </p>
        </div>
        <div className="relative overflow-hidden">
          <div className="flex marquee-track">
            <div className="flex gap-6 shrink-0">
              {(templates.length > 0 ? templates : TPL_FALLBACK).map((tpl) => {
                const id = typeof tpl === 'string' ? tpl : tpl.id;
                const label = TPL_NAMES[id] || id;
                const thumbUrl = templateThumbnails[id] || null;
                const isFree = templateFreeSet.has(id) || FREE_TEMPLATES.has(id);
                return (
                  <div
                    key={id}
                    className="flex-shrink-0 w-64 bg-white border border-zinc-200 rounded-card shadow-soft overflow-hidden"
                  >
                    <div className="aspect-[1/1.414] bg-white overflow-hidden">
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt={label}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-zinc-50 to-white p-4 flex flex-col">
                          <div className="flex items-center justify-between mb-3">
                            <div className="w-6 h-6 rounded-full bg-zinc-200" />
                            <div className="flex gap-1">
                              <div className="w-2 h-2 rounded-full bg-zinc-200" />
                              <div className="w-2 h-2 rounded-full bg-zinc-200" />
                            </div>
                          </div>
                          <div className="h-2 w-3/4 bg-zinc-200 rounded mb-2" />
                          <div className="h-2 w-1/2 bg-zinc-100 rounded mb-4" />
                          <div className="space-y-1.5 flex-1">
                            <div className="h-1.5 w-full bg-zinc-100 rounded" />
                            <div className="h-1.5 w-5/6 bg-zinc-100 rounded" />
                            <div className="h-1.5 w-2/3 bg-zinc-100 rounded" />
                          </div>
                          <div className="mt-auto pt-3 border-t border-zinc-100">
                            <div className="flex flex-wrap gap-1">
                              <div className="h-3 w-10 bg-zinc-100 rounded" />
                              <div className="h-3 w-8 bg-zinc-100 rounded" />
                              <div className="h-3 w-12 bg-zinc-100 rounded" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="px-3 py-2.5 border-t border-zinc-100">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">{label}</p>
                        {isFree && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                            Free
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-secondary-foreground">ATS-Optimized</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-6 shrink-0">
              {(templates.length > 0 ? templates : TPL_FALLBACK).map((tpl) => {
                const id = typeof tpl === 'string' ? tpl : tpl.id;
                const label = TPL_NAMES[id] || id;
                const thumbUrl = templateThumbnails[id] || null;
                const isFree = templateFreeSet.has(id) || FREE_TEMPLATES.has(id);
                return (
                  <div
                    key={id}
                    className="flex-shrink-0 w-64 bg-white border border-zinc-200 rounded-card shadow-soft overflow-hidden"
                  >
                    <div className="aspect-[1/1.414] bg-white overflow-hidden">
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt={label}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-zinc-50 to-white p-4 flex flex-col">
                          <div className="flex items-center justify-between mb-3">
                            <div className="w-6 h-6 rounded-full bg-zinc-200" />
                            <div className="flex gap-1">
                              <div className="w-2 h-2 rounded-full bg-zinc-200" />
                              <div className="w-2 h-2 rounded-full bg-zinc-200" />
                            </div>
                          </div>
                          <div className="h-2 w-3/4 bg-zinc-200 rounded mb-2" />
                          <div className="h-2 w-1/2 bg-zinc-100 rounded mb-4" />
                          <div className="space-y-1.5 flex-1">
                            <div className="h-1.5 w-full bg-zinc-100 rounded" />
                            <div className="h-1.5 w-5/6 bg-zinc-100 rounded" />
                            <div className="h-1.5 w-2/3 bg-zinc-100 rounded" />
                          </div>
                          <div className="mt-auto pt-3 border-t border-zinc-100">
                            <div className="flex flex-wrap gap-1">
                              <div className="h-3 w-10 bg-zinc-100 rounded" />
                              <div className="h-3 w-8 bg-zinc-100 rounded" />
                              <div className="h-3 w-12 bg-zinc-100 rounded" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="px-3 py-2.5 border-t border-zinc-100">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">{label}</p>
                        {isFree && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                            Free
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-secondary-foreground">ATS-Optimized</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-zinc-200">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-12">
          {[
            { value: '3.4x', label: 'Increase in interview conversion', icon: TrendingUp },
            { value: '14 min', label: 'Average time to build & export', icon: Clock },
            { value: '$22k+', label: 'Avg salary increase reported', icon: Users },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <stat.icon className="w-5 h-5 text-brand-500 mx-auto mb-3" />
              <div className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-zinc-500">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ PRICING ============ */}
      {paymentsEnabled && (
      <section id="pricing" className="py-20 scroll-mt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-zinc-500">Start free, upgrade when you need more.</p>
          </div>

          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-zinc-100 rounded-card p-1 gap-1">
              <button
                onClick={() => setBillingInterval('month')}
                className={`px-4 py-2 text-xs font-medium rounded-btn transition-all ${billingInterval === 'month' ? 'bg-white shadow-soft text-foreground' : 'text-zinc-600 hover:text-foreground'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('year')}
                className={`px-4 py-2 text-xs font-medium rounded-btn transition-all inline-flex items-center gap-1.5 ${billingInterval === 'year' ? 'bg-white shadow-soft text-foreground' : 'text-zinc-600 hover:text-foreground'}`}
              >
                Yearly
                {savingsPct > 0 && (
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Save {savingsPct}%</span>
                )}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="bg-white border border-zinc-200 rounded-card p-8">
              <h3 className="font-display font-semibold text-lg text-foreground mb-1">Free</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-display text-4xl font-semibold text-foreground">$0</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'Preview all templates',
                  'AI content enrichment',
                  'Job matching',
                  'Standard formatting',
                ].map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-sm text-zinc-600">
                    <Check className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    {feat}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToAuth('register')}
                className="w-full h-10 border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-sm font-medium rounded-btn transition-all duration-200"
              >
                Get Started Free
              </button>
            </div>

            <div className="bg-foreground rounded-card p-8 relative">
              {billingInterval === 'month' && (
                <div className="absolute -top-2.5 right-6 px-3 py-0.5 bg-brand-500 text-white text-[11px] font-semibold rounded-full">
                  Most Popular
                </div>
              )}
              <h3 className="font-display font-semibold text-lg text-white mb-1">Pro</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-display text-4xl font-semibold text-white">{priceDisplay}</span>
                <span className="text-zinc-400 text-sm">/{planInterval}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'Everything in Free',
                  'Unlimited resume generation',
                  'Premium executive templates',
                  'Advanced AI enrichment',
                  'Unlimited PDF downloads',
                  'Priority support',
                ].map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    {feat}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToAuth('register')}
                className="w-full h-10 bg-white hover:bg-zinc-100 text-foreground text-sm font-medium rounded-btn transition-all duration-200"
              >
                Subscribe Now
              </button>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============ FAQ ============ */}
      <section id="faq" className="py-20 scroll-mt-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-zinc-200">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground text-center mb-12">
            Frequently asked questions
          </h2>
          <div className="space-y-2">
            {[
              { q: 'Is my data private and secure?', a: 'Yes. Your resume data is encrypted at rest and in transit. We never share your personal information with third parties without your explicit consent.' },
              { q: 'Are the templates ATS-compatible?', a: 'Every template is engineered to pass through modern applicant tracking systems. We use semantic HTML structure, standard font stacks, and clean typography to ensure maximum compatibility.' },
              { q: 'Can I customize the formatting?', a: 'Pro users can fine-tune margins, font sizes, spacing, and color accents. The Free plan includes our curated templates with preset premium formatting.' },
              { q: 'What file formats are supported for export?', a: 'You can export as PDF for submissions, DOCX for further editing, and plain text for ATS parsing. All exports maintain perfect formatting fidelity.' },
              { q: 'Can I cancel my subscription anytime?', a: 'Yes. You can cancel anytime from your account settings. Your Pro features remain active until the end of your billing period.' },
            ].map((faq, i) => (
              <FAQItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOOTER CTA ============ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-foreground via-foreground to-brand-700">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-4">
            Ready to land your dream role?
          </h2>
          <p className="text-zinc-300 mb-8 max-w-lg mx-auto">
            Join thousands of professionals who've transformed their careers with AI-powered resumes.
          </p>
          <button
            onClick={() => scrollToAuth('register')}
            className="h-12 px-8 bg-white hover:bg-zinc-100 text-foreground text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2"
          >
            Get Started Instantly
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
      </main>

      {/* ============ FOOTER ============ */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-foreground border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-foreground" />
              </div>
              <span className="font-display font-semibold text-sm text-white">ResumeAI</span>
            </div>
            <div className="flex items-center gap-6 text-xs text-muted-foreground">
              <span>Product</span>
              <span>Templates</span>
              <span>Privacy</span>
              <span>Terms</span>
            </div>
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} ResumeAI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          animation: marquee 40s linear infinite;
          width: fit-content;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

/* ============ FAQ ACCORDION ITEM ============ */
const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-200 rounded-card overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-foreground hover:bg-zinc-50 transition-colors"
      >
        {question}
        <ChevronDown
          className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-4 text-sm text-zinc-500 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Landing;
